'use client'

import { Cell, InputSelect, InputText, Spacer } from '@vinted/web-ui'
import { find } from 'lodash'
import { useFormContext } from 'react-hook-form'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { CountryModel } from '@marketplace-web/country/country-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { InfoBanner } from '@marketplace-web/info-banner/info-banner-feature'
import {
  AddressField,
  AddressFieldConfiguration,
  AddressFieldTranslationModel,
  PostalCodeModel,
} from '@marketplace-web/address/address-data'

import { CityOption, Field, FormModel, PostalCodeConstraint } from '../types'

type Props = {
  fieldConfiguration: Array<AddressFieldConfiguration>
  postalCodeConfiguration: PostalCodeModel | null
  postalCodeConstraints: PostalCodeConstraint | null
  countries: Array<CountryModel>
  countryId?: number | null
  postalCode?: string | null
  city?: string | null
  translations?: AddressFieldTranslationModel
  infoBannerProps?: React.ComponentProps<typeof InfoBanner>
  onBlur: (field: AddressField, value: string) => void
}

const AddressFormFields = ({
  fieldConfiguration,
  postalCodeConfiguration,
  postalCodeConstraints,
  countries,
  countryId,
  postalCode,
  city,
  translations,
  infoBannerProps,
  onBlur,
}: Props) => {
  const {
    formState: { errors },
    register,
    setValue,
  } = useFormContext<FormModel>()

  const translate = useTranslate('address_form')
  const validationMessage = useFormValidationMessage(errors, 'address_form')

  const getCountriesOptions = () =>
    countries.map(country => ({ value: country.id, label: country.title }))

  const getCityOptions = (): Array<CityOption> => {
    if (!postalCodeConfiguration) return []

    const { cities } = postalCodeConfiguration
    const emptyOption = { value: '', label: '' }
    const options = cities.map((label, index) => ({ value: cities[index]!, label }))

    return options.length > 1 ? [emptyOption, ...options] : options
  }

  const renderNameField = () => {
    const config = fieldConfiguration?.find(({ field }) => field === AddressField.Name)

    if (!config) return null

    return (
      <InputText
        {...register(Field.Name, {
          required: config.required,
          maxLength: 50,
          onBlur: event => onBlur(AddressField.Name, event.target.value),
        })}
        title={translations?.name?.title || translate('name.title')}
        placeholder={translations?.name?.placeholder || translate('name.placeholder')}
        styling="tight"
        validation={renderValidation(validationMessage(Field.Name))}
        disabled={config.disabled}
        key={Field.Name}
        testId="address-form-field-name"
        autoComplete="name"
      />
    )
  }

  const renderCountryField = () => {
    const config = fieldConfiguration?.find(({ field }) => field === AddressField.Country)

    if (!config) return null

    return (
      <InputSelect
        {...register(Field.CountryId, {
          required: config.required,
          onBlur: event => onBlur(AddressField.Country, event.target.value),
        })}
        options={getCountriesOptions()}
        title={translations?.country?.title || translate('country.title')}
        styling="tight"
        validation={renderValidation(validationMessage(Field.CountryId))}
        value={countryId?.toString() || ''}
        disabled={config.disabled}
        key={Field.CountryId}
        testId="address-form-field-country"
        onChange={event => {
          setValue(Field.CountryId, Number.parseInt(event.target.value, 10))
        }}
      />
    )
  }

  const renderLine1Field = () => {
    const config = fieldConfiguration?.find(({ field }) => field === AddressField.Line1)

    if (!config) return null

    return (
      <InputText
        {...register(Field.Line1, {
          required: config.required,
          maxLength: 50,
          onBlur: event => onBlur(AddressField.Line1, event.target.value),
        })}
        title={translations?.line1?.title || translate('line1.title')}
        placeholder={translations?.line1?.placeholder || translate('line1.placeholder')}
        styling="tight"
        validation={renderValidation(validationMessage(Field.Line1))}
        disabled={config.disabled}
        key={Field.Line1}
        testId="address-form-field-line1"
        autoComplete="address-line1"
      />
    )
  }

  const renderLine2Field = () => {
    const config = fieldConfiguration?.find(({ field }) => field === AddressField.Line2)

    if (!config) return null

    return (
      <InputText
        {...register(Field.Line2, {
          required: config.required,
          maxLength: 50,
          onBlur: event => onBlur(AddressField.Line2, event.target.value),
        })}
        title={translations?.line2?.title || translate('line2.title')}
        placeholder={translations?.line2?.placeholder || translate('line2.placeholder')}
        styling="tight"
        validation={renderValidation(validationMessage(Field.Line2))}
        disabled={config.disabled}
        key={Field.Line2}
        testId="address-form-field-line2"
        autoComplete="address-line2"
      />
    )
  }

  const renderPostalCodeField = () => {
    const config = fieldConfiguration?.find(({ field }) => field === AddressField.PostalCode)

    if (!config || !postalCodeConstraints) return null

    return (
      <InputText
        {...register(Field.PostalCode, {
          required: config.required,
          onBlur: event => onBlur(AddressField.PostalCode, event.target.value),
        })}
        title={translations?.postalCode?.title || translate('postal_code.title')}
        placeholder={translations?.postalCode?.placeholder || translate('postal_code.placeholder')}
        styling="tight"
        validation={renderValidation(validationMessage(Field.PostalCode))}
        maxLength={postalCodeConstraints?.maxLength}
        disabled={config.disabled}
        key={Field.PostalCode}
        testId="address-form-field-postal-code"
        autoComplete="postal-code"
      />
    )
  }

  const renderCityField = () => {
    const config = fieldConfiguration?.find(({ field }) => field === AddressField.City)

    if (!config || !postalCode || !postalCodeConfiguration) return null

    const getCityId = (cityString?: string | null) => {
      return cityString ? find(getCityOptions(), { label: cityString })?.value || '' : ''
    }

    const cityId = getCityId(city)

    return (
      <InputSelect
        {...register(Field.City, {
          required: config.required,
          onBlur: event => onBlur(AddressField.City, event.target.value),
        })}
        options={getCityOptions()}
        title={translations?.city?.title || translate('city.title')}
        styling="tight"
        value={cityId ?? ''}
        validation={renderValidation(validationMessage(Field.City))}
        disabled={config.disabled}
        key={Field.City}
        testId="address-form-field-city"
        onChange={event => {
          setValue(Field.City, event.target.value, { shouldValidate: true, shouldDirty: true })
        }}
      />
    )
  }

  const renderField = ({ field }: AddressFieldConfiguration) => {
    switch (field) {
      case AddressField.Name:
        return renderNameField()
      case AddressField.Country:
        return renderCountryField()
      case AddressField.Line1:
        return renderLine1Field()
      case AddressField.Line2:
        return renderLine2Field()
      case AddressField.PostalCode:
        return renderPostalCodeField()
      case AddressField.City:
        return renderCityField()
      default:
        return null
    }
  }

  return (
    <Cell>
      {!!infoBannerProps && <InfoBanner {...infoBannerProps} />}
      <SeparatedList separator={<Spacer size="x-large" />}>
        {fieldConfiguration.map(renderField)}
      </SeparatedList>
    </Cell>
  )
}

export default AddressFormFields
